import React, { useEffect, useState } from "react";
import { ArrowRightLeft, Search as SearchIcon, Loader2, Calendar, Clock } from "lucide-react";
import StationAutocomplete from "../components/StationAutocomplete";
import CityAutocomplete from "../components/CityAutocomplete";
import DateTimePicker from "../components/DateTimePicker";
import ItineraryCard from "../components/ItineraryCard";
import RouteBuilder from "../components/RouteBuilder";
import Footer from "../components/Footer";
import { api, formatApiError } from "../lib/api";
import { useRouteBuilder } from "../context/RouteContext";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

function defaultDateTime() {
  // Default to TOMORROW at 09:00 local time (clamped into Transitous timetable window)
  const target = new Date();
  target.setDate(target.getDate() + 1);
  target.setHours(9, 0, 0, 0);
  const min = new Date(todayISO());
  const max = new Date("2027-05-10T23:59:00");
  let final = target;
  if (final < min) final = min;
  if (final > max) final = max;
  const pad = (n) => n.toString().padStart(2, "0");
  return `${final.getFullYear()}-${pad(final.getMonth() + 1)}-${pad(final.getDate())}T${pad(final.getHours())}:${pad(final.getMinutes())}`;
}

const todayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 16);
};

export default function Search() {
  const [params] = useSearchParams();
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const [when, setWhen] = useState(defaultDateTime());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);
  const { addSegment } = useRouteBuilder();

  useEffect(() => {
    // Optional ?from=&to= prefill via slug from City modal CTA
    const f = params.get("from_id");
    const t = params.get("to_id");
    const fn = params.get("from_name");
    const tn = params.get("to_name");
    if (f && fn) setFrom({ id: f, name: fn });
    if (t && tn) setTo({ id: t, name: tn });
  }, [params]);

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const doSearch = async (e) => {
    e?.preventDefault();
    setError("");
    if (!from?.id || !to?.id) {
      setError("Pick a station for both From and To.");
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const iso = new Date(when).toISOString();
      const { data } = await api.get("/plan", {
        params: { from: from.id, to: to.id, time: iso, num: 5 },
      });
      setResults(data);
      if (data.itineraries.length === 0) {
        toast.message("No itineraries found", { description: "Try a different time or date." });
      }
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = (seg) => {
    addSegment(seg);
    toast.success(`Added ${seg.from_city} → ${seg.to_city}`);
  };

  return (
    <div data-testid="search-page" className="pt-safe-navbar pb-12">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="mb-6">
          <div className="iq-overline mb-2">Step one</div>
          <h1 className="font-display text-cream text-4xl md:text-5xl tracking-wider">Plan a leg</h1>
        </div>

        <form
          data-testid="search-form"
          onSubmit={doSearch}
          className="iq-card p-5 md:p-6 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_auto] gap-3 md:gap-4 md:items-end"
        >
          <div>
            <label className="iq-label">From</label>
            <CityAutocomplete value={from} onChange={setFrom} placeholder="Pick a city…" testId="search-from" />
          </div>
          <div className="hidden md:flex md:pb-1 justify-center">
            <button
              type="button"
              onClick={swap}
              data-testid="search-swap"
              className="iq-btn-ghost border border-panel-border rounded-sm hover:border-gold/40 hover:text-gold"
              title="Swap"
            >
              <ArrowRightLeft className="h-4 w-4" />
            </button>
          </div>
          <div>
            <label className="iq-label">To</label>
            <CityAutocomplete value={to} onChange={setTo} placeholder="Pick a city…" testId="search-to" />
          </div>
          <div className="grid grid-cols-2 md:block gap-3 md:gap-0 items-end">
            <div className="md:contents col-span-2">
              <label className="iq-label">Depart</label>
              <DateTimePicker
                value={when}
                onChange={setWhen}
                min={todayISO()}
                max="2027-05-10T23:59"
                testId="search-dt"
              />
            </div>
            <button type="button" onClick={swap} className="md:hidden iq-btn-ghost border border-panel-border rounded-sm justify-center col-span-2" title="Swap">
              <ArrowRightLeft className="h-4 w-4" /> Swap
            </button>
          </div>
          <button data-testid="search-submit" type="submit" disabled={loading} className="iq-btn-primary text-base h-[46px] w-full md:w-auto">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <SearchIcon className="h-5 w-5" />}
            Search
          </button>
        </form>

        {error && (
          <div data-testid="search-error" className="mt-4 border border-accent-red/40 bg-accent-red/10 text-cream px-4 py-3 rounded-sm text-sm">
            {error}
          </div>
        )}

        <div className="mt-6 text-xs text-cream-dim/70 flex items-center gap-4 flex-wrap">
          <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-gold" /> Live timetables: Apr 2026 → May 2027</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-gold" /> Times in local timezone (UTC inputs)</span>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div data-testid="results-list" className="space-y-3 min-h-[120px]">
            {loading && (
              <div className="iq-card p-12 text-center">
                <Loader2 className="h-8 w-8 text-gold mx-auto animate-spin mb-3" />
                <p className="text-cream-dim font-serif italic">Consulting the timetables…</p>
              </div>
            )}
            {!loading && results && results.itineraries.length === 0 && (
              <div className="iq-card p-12 text-center">
                <p className="text-cream-dim font-serif italic">No journeys for that time. Try a different date or station.</p>
              </div>
            )}
            {!loading && results && results.itineraries.length > 0 && (
              <>
                <div className="iq-overline">
                  {results.itineraries.length} journeys · {results.from?.name} → {results.to?.name}
                </div>
                {results.itineraries.map((itin, i) => (
                  <ItineraryCard
                    key={`${itin.start_time || ""}-${itin.end_time || ""}-${i}`}
                    index={i}
                    itin={itin}
                    fromName={results.from?.name || from?.name}
                    toName={results.to?.name || to?.name}
                    onAddToRoute={handleAdd}
                  />
                ))}
              </>
            )}
            {!loading && !results && (
              <div className="iq-card p-12 text-center">
                <p className="text-cream-dim font-serif italic max-w-md mx-auto leading-relaxed">
                  Where would you like to wake up tomorrow? Pick a departure, a destination, a time. The board will roll.
                </p>
              </div>
            )}
          </div>
          <div>
            <RouteBuilder />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
